import { useEffect, useState } from "react";
import { Trash2, X, Pencil, Check, Send } from "lucide-react";
import {
  getUsers,
  updateUserRole,
  deleteUser,
  getInvitations,
  createInvitation,
  cancelInvitation,
  resendInvitation,
  type User,
  type Invitation,
} from "../api";
import Timestamp from "../components/Timestamp";
import { useToast } from "../components/Toast";
import Tooltip from "../components/Tooltip";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import { useAuth } from "../contexts/AuthContext";
import { formatFutureRelativeTime } from "../utils/date";
import { usePageTitle } from "../hooks/usePageTitle";

export default function UsersPage() {
  usePageTitle("Users");
  const { user: currentUser } = useAuth();
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Edit role state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<"admin" | "developer" | "readonly" | null>(null);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "developer" | "readonly">("readonly");
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [usersData, invitationsData] = await Promise.all([getUsers(), getInvitations()]);
      setUsers(usersData);
      setInvitations(invitationsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  function handleStartEdit(user: User) {
    setEditingUserId(user.id);
    setPendingRole(user.role);
    setShowDeleteConfirm(null);
  }

  function handleCancelEdit() {
    setEditingUserId(null);
    setPendingRole(null);
  }

  async function handleSaveRole(userId: string) {
    if (!pendingRole) return;
    try {
      setUpdatingId(userId);
      const updated = await updateUserRole(userId, pendingRole);
      setUsers(users.map((u) => (u.id === userId ? updated : u)));
      addToast(`Role updated to ${pendingRole}`, "success");
      setEditingUserId(null);
      setPendingRole(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to update role", "error");
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleDelete(userId: string) {
    const user = users.find((u) => u.id === userId);
    try {
      setDeletingId(userId);
      await deleteUser(userId);
      setUsers(users.filter((u) => u.id !== userId));
      addToast(`User ${user?.email} removed`, "success");
      setShowDeleteConfirm(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to delete user", "error");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    try {
      setInviting(true);
      const invitation = await createInvitation(inviteEmail, inviteRole);
      setInvitations([invitation, ...invitations]);
      addToast(`Invitation sent to ${inviteEmail}`, "success");
      setInviteEmail("");
      setInviteRole("readonly");
      setShowInviteForm(false);
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to send invitation", "error");
    } finally {
      setInviting(false);
    }
  }

  async function handleCancelInvitation(invitationId: string) {
    const invitation = invitations.find((i) => i.id === invitationId);
    try {
      setCancellingId(invitationId);
      await cancelInvitation(invitationId);
      setInvitations(invitations.filter((i) => i.id !== invitationId));
      addToast(`Invitation to ${invitation?.email} cancelled`, "success");
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to cancel invitation", "error");
    } finally {
      setCancellingId(null);
    }
  }

  async function handleResendInvitation(invitationId: string) {
    const invitation = invitations.find((i) => i.id === invitationId);
    try {
      setResendingId(invitationId);
      await resendInvitation(invitationId);
      addToast(`Invitation resent to ${invitation?.email}`, "success");
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to resend invitation", "error");
    } finally {
      setResendingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Breadcrumb to="/apps" />
        <PageHeader.TitleRow>
          <PageHeader.TitleGroup>
            <PageHeader.Title>Manage Users</PageHeader.Title>
            <PageHeader.Subtitle>
              {users.length} user{users.length !== 1 ? "s" : ""} with access
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            <Button onClick={() => setShowInviteForm(!showInviteForm)}>
              {showInviteForm ? "Cancel" : "Invite User"}
            </Button>
          </PageHeader.Actions>
        </PageHeader.TitleRow>
      </PageHeader>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">
            Invite User
          </h2>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="invite-email" className="sr-only">
                Email address
              </label>
              <input
                id="invite-email"
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                autoFocus
                autoComplete="off"
                data-1p-ignore
                className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
              />
            </div>
            <div>
              <label htmlFor="invite-role" className="sr-only">
                Role
              </label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as "admin" | "developer" | "readonly")
                }
                className="w-full sm:w-auto pl-4 pr-8 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text appearance-none bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center]"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                }}
              >
                <option value="readonly">Read-only</option>
                <option value="developer">Developer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting || !inviteEmail}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {inviting ? "Sending..." : "Send Invitation"}
            </button>
          </form>
          <p className="mt-3 text-sm text-gray-500 dark:text-dark-text-secondary">
            An email invitation will be sent. They&apos;ll need to sign in with Google using this
            email address.
          </p>
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary">
            <h2 className="font-semibold text-gray-900 dark:text-dark-text">
              Pending Invitations ({invitations.length})
            </h2>
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-dark-border">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-dark-text">
                    {invitation.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-secondary">
                    {invitation.role === "admin"
                      ? "Admin"
                      : invitation.role === "developer"
                        ? "Developer"
                        : "Read-only"}{" "}
                    · Expires {formatFutureRelativeTime(invitation.expiresAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip content="Resend invitation" position="top">
                    <button
                      onClick={() => handleResendInvitation(invitation.id)}
                      disabled={resendingId === invitation.id}
                      className="p-1.5 text-gray-400 dark:text-dark-text-muted hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Cancel invitation" position="top">
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      disabled={cancellingId === invitation.id}
                      className="p-1.5 text-gray-400 dark:text-dark-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary">
          <h2 className="font-semibold text-gray-900 dark:text-dark-text">Current Users</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
          <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                Last Login
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
            {users.map((user) => {
              const isCurrentUser = currentUser?.id === user.id;
              return (
                <tr
                  key={user.id}
                  className={isCurrentUser ? "bg-primary-50/30 dark:bg-primary-900/10" : ""}
                >
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="w-8 h-8 rounded-full"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-dark-bg-tertiary flex items-center justify-center text-sm font-medium text-gray-600 dark:text-dark-text-secondary">
                          {user.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
                          {user.name}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-dark-text-muted">
                              (you)
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-dark-text-secondary">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-center">
                    {editingUserId === user.id ? (
                      <select
                        value={pendingRole || user.role}
                        onChange={(e) =>
                          setPendingRole(e.target.value as "admin" | "developer" | "readonly")
                        }
                        disabled={updatingId === user.id}
                        className={`text-sm border border-gray-300 dark:border-dark-border rounded-md pl-2 pr-7 py-1 appearance-none bg-no-repeat bg-[length:14px_14px] bg-[right_4px_center] bg-white dark:bg-dark-bg-tertiary dark:text-dark-text ${updatingId === user.id ? "opacity-50" : ""}`}
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        }}
                      >
                        <option value="admin">Admin</option>
                        <option value="developer">Developer</option>
                        <option value="readonly">Read-only</option>
                      </select>
                    ) : (
                      <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                        {user.role === "admin"
                          ? "Admin"
                          : user.role === "developer"
                            ? "Developer"
                            : "Read-only"}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-center text-sm text-gray-500 dark:text-dark-text-secondary">
                    {user.lastLoginAt ? <Timestamp date={user.lastLoginAt} /> : "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-center text-sm text-gray-500 dark:text-dark-text-secondary">
                    <Timestamp date={user.createdAt} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap align-middle text-center">
                    {editingUserId === user.id ? (
                      <div className="inline-flex items-center gap-1">
                        <Tooltip content="Save" position="top">
                          <button
                            onClick={() => handleSaveRole(user.id)}
                            disabled={updatingId === user.id || pendingRole === user.role}
                            className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Cancel" position="top">
                          <button
                            onClick={handleCancelEdit}
                            disabled={updatingId === user.id}
                            className="p-1.5 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    ) : showDeleteConfirm === user.id ? (
                      <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-red-700 dark:text-red-300">Remove?</span>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={deletingId === user.id}
                          className="px-2 py-0.5 bg-red-500 text-white text-xs rounded font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                        >
                          {deletingId === user.id ? "..." : "Yes"}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="px-2 py-0.5 bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary text-xs rounded font-medium hover:bg-gray-100 dark:hover:bg-dark-border transition-colors border border-gray-300 dark:border-dark-border"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <Tooltip
                          content={isCurrentUser ? "Cannot edit your own role" : "Edit role"}
                          position="top"
                        >
                          <button
                            onClick={() => handleStartEdit(user)}
                            disabled={isCurrentUser}
                            className={`p-1.5 rounded transition-colors ${
                              isCurrentUser
                                ? "text-gray-300 dark:text-dark-text-muted cursor-not-allowed"
                                : "text-gray-400 dark:text-dark-text-muted hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                            }`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip
                          content={isCurrentUser ? "Cannot remove yourself" : "Remove user"}
                          position="top"
                        >
                          <button
                            onClick={() => setShowDeleteConfirm(user.id)}
                            disabled={isCurrentUser}
                            className={`p-1.5 rounded transition-colors ${
                              isCurrentUser
                                ? "text-gray-300 dark:text-dark-text-muted cursor-not-allowed"
                                : "text-gray-400 dark:text-dark-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            }`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
